import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon
} from "@mui/icons-material";
import TableSkeleton from "./TableSkeleton";
import EmptyStateRow from "./EmptyStateRow";
import ErrorStateRow from "./ErrorStateRow";

function defaultGetRowKey(row) {
  return row?.id ?? JSON.stringify(row);
}

// A column is "secondary" when it can be folded away on narrow screens.
// Anything without an explicit priority is treated as primary (always visible),
// so existing tables keep behaving exactly as before until columns are tagged.
function isSecondary(col) {
  return col?.priority === "secondary";
}

// Column labels may contain "\n" to break the header into two lines; such a
// column (or one tagged `compact: true`) shrinks to its content width so a
// short value (date, count, status) no longer pays for a long one-line title.
function isCompact(col) {
  return col?.compact === true || (typeof col?.label === "string" && col.label.includes("\n"));
}

function plainLabel(col) {
  return typeof col?.label === "string" ? col.label.replace(/\n/g, " ") : col?.label;
}

// Cell renderers often return a flex <Box> or a block with its own maxWidth,
// which ignore the cell's text-align. Lay the content out with flexbox so the
// body follows the same edge as the header for every `align` value.
// Column `align` is logical: "start" (the reading edge — right in this RTL
// app), "end", or "center" (default). The app's stylis RTL plugin mirrors
// physical left/right in CSS, so MUI's physical values are written LTR-style
// ("left" = start) and the flex wrapper uses the direction-aware keywords.
function cellAlign(align) {
  if (align === "start" || align === "left") return "left";
  if (align === "end" || align === "right") return "right";
  return "center";
}

function justifyFor(align) {
  if (align === "start" || align === "left") return "flex-start";
  if (align === "end" || align === "right") return "flex-end";
  return "center";
}

function renderCellValue(col, row) {
  return typeof col.render === "function" ? col.render(row) : (row?.[col.id] ?? "—");
}

export default function StandardTable({
  columns = [],
  rows = [],
  loading = false,
  error,
  emptyText = "رکوردی یافت نشد.",
  onRowClick,
  getRowKey = defaultGetRowKey,
  ariaLabel,
  dense = true,
  minWidth = 400,
  getRowAriaLabel,
  getRowProps,
  stickyHeader = true,
  // Constrain the table to its own scroll viewport so the horizontal scrollbar
  // and the (sticky) header stay reachable without scrolling the whole page.
  // Pass `false` to opt out (e.g. tables embedded in an already-scrolling panel).
  maxHeight = "calc(100vh - 240px)",
  containerSx,
  tableSx,
  // Optional summary/footer content. Provide a `<TableRow>` (or fragment of
  // rows) with cells whose colSpans match the column count. Rendered only in
  // the desktop table view, not in the mobile card view.
  footer,
  // Optional per-row detail rendered inside the expander (and at the bottom of
  // each mobile card). When provided, every row gets the expand chevron even if
  // no columns are folded — use it to surface extra fields (e.g. the full
  // profile) without opening an edit dialog.
  renderRowDetail
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const size = dense ? "small" : "medium";

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = useCallback((key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Secondary columns always fold behind the per-row expander on the desktop
  // table, so every table looks consistent regardless of how much room it has.
  // (The mobile card view below shows all columns stacked.)
  const visibleColumns = useMemo(
    () => columns.filter((c) => !isSecondary(c)),
    [columns]
  );
  const hiddenColumns = useMemo(
    () => columns.filter(isSecondary),
    [columns]
  );
  const hasRowDetail = typeof renderRowDetail === "function";
  const expandable = hiddenColumns.length > 0 || hasRowDetail;

  // +1 for the expander toggle column when some columns are folded away.
  const columnsCount = (visibleColumns.length || 1) + (expandable ? 1 : 0);

  const handleRowKeyDown = useCallback((event, row) => {
    if (!onRowClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick(row);
    }
  }, [onRowClick]);

  const mergedContainerSx = {
    mt: 2,
    ...(maxHeight ? { maxHeight } : {}),
    ...(containerSx || {})
  };

  // ----- Mobile: stacked cards instead of a horizontally scrolling table -----
  if (isMobile) {
    return (
      <Box sx={{ mt: 2 }}>
        {loading && (
          <Stack spacing={1.5}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Paper key={`card-skeleton-${i}`} sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="75%" />
              </Paper>
            ))}
          </Stack>
        )}

        {!loading && error && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="error">
              {typeof error === "string" ? error : (error?.message || "خطای نامشخص")}
            </Typography>
          </Paper>
        )}

        {!loading && !error && rows.length === 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {emptyText}
            </Typography>
          </Paper>
        )}

        {!loading && !error && rows.map((row) => {
          const key = getRowKey(row);
          const clickable = typeof onRowClick === "function";
          // First column acts as the card title; everything else becomes label/value rows.
          const [titleCol, ...restCols] = columns;
          return (
            <Paper
              key={key}
              onClick={clickable ? () => onRowClick(row) : undefined}
              sx={{
                p: 2,
                mb: 1.5,
                ...(clickable ? { cursor: "pointer" } : {})
              }}
            >
              {titleCol && (
                <Box sx={{ mb: 1, fontWeight: "bold" }}>
                  {renderCellValue(titleCol, row)}
                </Box>
              )}
              <Stack spacing={0.75}>
                {restCols.map((col) => (
                  <Box
                    key={`${key}-${col.id}`}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {plainLabel(col)}
                    </Typography>
                    <Box sx={{ textAlign: "end", minWidth: 0, overflowWrap: "anywhere" }}>{renderCellValue(col, row)}</Box>
                  </Box>
                ))}
                {hasRowDetail && renderRowDetail(row)}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    );
  }

  // ----- Desktop / tablet: table with sticky header + foldable columns -----
  return (
    <TableContainer component={Paper} sx={mergedContainerSx}>
      <Table
        size={size}
        stickyHeader={stickyHeader}
        aria-label={ariaLabel}
        sx={{
          minWidth,
          // The theme gives head cells an opaque tinted background
          // (background.table), so scrolled rows don't ghost through the
          // sticky header; keep it above row content such as chips.
          "& .MuiTableCell-stickyHeader": {
            zIndex: 3
          },
          ...(tableSx || {})
        }}
      >
        <TableHead>
          <TableRow>
            {expandable && <TableCell sx={{ width: 48 }} aria-hidden />}
            {visibleColumns.map((col) => (
              <TableCell
                key={col.id}
                align={cellAlign(col.align)}
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.3,
                  ...(col.width ? { width: col.width } : isCompact(col) ? { width: "1%" } : {})
                }}
              >
                {col.renderHeader || col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableSkeleton columnsCount={columnsCount} rowsCount={3} />
          )}

          {!loading && error && (
            <ErrorStateRow colSpan={columnsCount} error={error} />
          )}

          {!loading && !error && rows.length === 0 && (
            <EmptyStateRow colSpan={columnsCount} text={emptyText} />
          )}

          {!loading && !error && rows.length > 0 && rows.map((row) => {
            const key = getRowKey(row);
            const clickable = typeof onRowClick === "function";
            const rowAriaLabel = getRowAriaLabel
              ? getRowAriaLabel(row)
              : (clickable ? "انتخاب" : undefined);
            const extraRowProps = typeof getRowProps === "function" ? (getRowProps(row) || {}) : {};
            const isOpen = !!expanded[key];
            return (
              <React.Fragment key={key}>
                <TableRow
                  hover={clickable}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  onKeyDown={clickable ? (e) => handleRowKeyDown(e, row) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? "button" : undefined}
                  aria-label={rowAriaLabel}
                  sx={clickable ? { cursor: "pointer", ...(extraRowProps.sx || {}) } : (extraRowProps.sx || undefined)}
                  selected={extraRowProps.selected}
                >
                  {expandable && (
                    <TableCell sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        aria-label={isOpen ? "بستن جزئیات" : "نمایش جزئیات"}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(key);
                        }}
                      >
                        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  )}
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={`${key}-${col.id}`}
                      align={cellAlign(col.align)}
                      sx={isCompact(col) ? { whiteSpace: "nowrap" } : undefined}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: justifyFor(col.align),
                          gap: 0.5,
                          minWidth: 0
                        }}
                      >
                        {renderCellValue(col, row)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>

                {expandable && (
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : "none" }} colSpan={columnsCount}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            py: 1.5,
                            display: "grid",
                            gridTemplateColumns: { sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
                            gap: 1.5
                          }}
                        >
                          {hiddenColumns.map((col) => (
                            <Box key={`${key}-detail-${col.id}`} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                {plainLabel(col)}:
                              </Typography>
                              <Box>{renderCellValue(col, row)}</Box>
                            </Box>
                          ))}
                          {hasRowDetail && renderRowDetail(row)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
        {!loading && !error && rows.length > 0 && footer && (
          <TableFooter>{footer}</TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
